import React, { useEffect, useState } from 'react';
import { X, Warning, PaperPlaneTilt, Check } from '@phosphor-icons/react';
import { cn, reportRentalIssue } from '../../lib/utils';
const REASONS = [{
  value: 'no_response',
  label: 'Chiriașul nu mai răspunde'
}, {
  value: 'mistake',
  label: 'Greșeală — nu am închiriat'
}, {
  value: 'unavailable',
  label: 'Proprietatea nu mai e disponibilă'
}, {
  value: 'other',
  label: 'Altă problemă'
}] as const;
interface ReportRentalIssueModalProps {
  isOpen: boolean;
  onClose: () => void;
  offerId: string;
  tenantName?: string;
  propertyTitle?: string;
  onSubmitted?: () => void;
}
/**
 * Lightweight "Ceva nu e în regulă" modal. Pre-fills offer/tenant/property
 * context, captures a reason + optional details, mock-submits to support
 * (no state mutation on the offer itself).
 */
export function ReportRentalIssueModal({
  isOpen,
  onClose,
  offerId,
  tenantName,
  propertyTitle,
  onSubmitted
}: ReportRentalIssueModalProps) {
  const [reason, setReason] = useState<string>(REASONS[0].value);
  const [details, setDetails] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [isOpen, onClose]);
  useEffect(() => {
    if (isOpen) {
      setReason(REASONS[0].value);
      setDetails('');
      setSubmitting(false);
      setSubmitted(false);
    }
  }, [isOpen]);
  if (!isOpen) return null;
  const handleSubmit = () => {
    setSubmitting(true);
    const label = REASONS.find(r => r.value === reason)?.label || reason;
    reportRentalIssue(offerId, label, details.trim() || undefined);
    setTimeout(() => {
      setSubmitting(false);
      setSubmitted(true);
      onSubmitted?.();
      setTimeout(() => {
        onClose();
      }, 1400);
    }, 400);
  };
  return <div role="dialog" aria-modal="true" aria-label="Raportează o problemă" className="fixed inset-0 z-[100] bg-dark-1/80 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4 lg:pl-[16rem] animate-in fade-in duration-200" onClick={onClose} data-id="element-1790">
      <div className="w-full sm:max-w-md bg-dark-2 sm:rounded-2xl rounded-t-2xl border-t sm:border border-dark-3 max-h-[90vh] flex flex-col animate-in slide-in-from-bottom-4 sm:zoom-in-95 duration-200" onClick={e => e.stopPropagation()} data-id="element-1791">
        <header className="flex items-center justify-between px-5 py-4 border-b border-dark-3 shrink-0" data-id="element-1792">
          <div className="flex items-center gap-2 min-w-0" data-id="element-1793">
            <Warning size={18} weight="regular" className="text-light-2 shrink-0" data-id="element-1794" />
            <h2 className="text-base font-semibold text-light-1" data-id="element-1795">
              Ceva nu e în regulă
            </h2>
          </div>
          <button type="button" onClick={onClose} aria-label="Închide" className="w-9 h-9 inline-flex items-center justify-center rounded-md text-light-3 hover:text-light-1 hover:bg-dark-3 transition-colors cursor-pointer" data-id="element-1796">
            <X size={18} weight="bold" data-id="element-1797" />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto px-5 py-5 space-y-5" data-id="element-1798">
          {submitted ? <div className="text-center py-6 space-y-3" data-id="element-1799">
              <div className="w-14 h-14 rounded-2xl bg-lime-5 border border-lime-4 inline-flex items-center justify-center mx-auto" data-id="element-1800">
                <Check size={28} weight="bold" className="text-lime-1" data-id="element-1801" />
              </div>
              <h3 className="text-base font-semibold text-light-1" data-id="element-1802">
                Mulțumim!
              </h3>
              <p className="text-sm text-light-3 max-w-xs mx-auto leading-relaxed" data-id="element-1803">
                Echipa Izio va prelua sesizarea ta și va reveni cât de curând.
              </p>
            </div> : <>
              <p className="text-sm text-light-3 leading-relaxed" data-id="element-1804">
                Spune-ne ce s-a întâmplat. Un coleg de la suport se va ocupa să
                rezolve situația.
              </p>

              {(tenantName || propertyTitle) && <div className="rounded-xl bg-dark-3 border border-dark-4 p-3 text-xs space-y-1" data-id="element-1805">
                  {tenantName && <p data-id="element-1806">
                      <span className="text-light-2" data-id="element-1807">Chiriaș:</span>{' '}
                      <span className="text-light-1 font-medium" data-id="element-1808">
                        {tenantName}
                      </span>
                    </p>}
                  {propertyTitle && <p data-id="element-1809">
                      <span className="text-light-2" data-id="element-1810">Proprietate:</span>{' '}
                      <span className="text-light-1 font-medium" data-id="element-1811">
                        {propertyTitle}
                      </span>
                    </p>}
                </div>}

              <fieldset className="space-y-2" data-id="element-1812">
                <legend className="text-xs font-medium text-light-3 uppercase tracking-wider mb-1" data-id="element-1813">
                  Motiv
                </legend>
                {REASONS.map(r => <label key={r.value} className={cn('flex items-center gap-3 px-3 py-2.5 rounded-md border cursor-pointer transition-colors', reason === r.value ? 'bg-dark-3 border-lime-4' : 'bg-dark-2 border-dark-4 hover:border-light-4')} data-id="element-1814">
                    <input type="radio" name="report-reason" value={r.value} checked={reason === r.value} onChange={() => setReason(r.value)} className="sr-only" data-id="element-1815" />
                    <span className={cn('w-4 h-4 rounded-full border-2 inline-flex items-center justify-center shrink-0 transition-colors', reason === r.value ? 'border-lime-1' : 'border-light-4')} data-id="element-1816">
                      {reason === r.value && <span className="w-2 h-2 rounded-full bg-lime-1" data-id="element-1817" />}
                    </span>
                    <span className="text-sm text-light-1" data-id="element-1818">{r.label}</span>
                  </label>)}
              </fieldset>

              <div data-id="element-1819">
                <label htmlFor="report-details" className="block text-xs font-medium text-light-3 uppercase tracking-wider mb-1.5" data-id="element-1820">
                  Detalii (opțional)
                </label>
                <textarea id="report-details" value={details} onChange={e => setDetails(e.target.value)} rows={3} placeholder="Spune-ne mai multe ca să putem ajuta mai repede..." className="w-full rounded-md bg-dark-3 border border-dark-4 px-3 py-2 text-sm text-light-1 placeholder:text-light-4 outline-none focus:border-lime-4 transition-colors resize-none" data-id="element-1821" />
              </div>
            </>}
        </div>

        {!submitted && <div className="px-5 py-4 border-t border-dark-3 shrink-0 flex gap-2" data-id="element-1822">
            <button type="button" onClick={onClose} className="flex-1 h-11 rounded-md bg-dark-3 hover:bg-dark-4 border border-dark-4 text-light-1 text-sm font-medium transition-colors cursor-pointer" data-id="element-1823">
              Anulează
            </button>
            <button type="button" onClick={handleSubmit} disabled={submitting} className={cn('flex-1 h-11 rounded-md inline-flex items-center justify-center gap-2 text-sm font-semibold transition-colors', submitting ? 'bg-dark-3 text-light-4 cursor-wait' : 'bg-lime-1 hover:bg-lime-2 text-dark-1 cursor-pointer')} data-id="element-1824">
              <PaperPlaneTilt size={14} weight="bold" data-id="element-1825" />
              {submitting ? 'Se trimite...' : 'Trimite'}
            </button>
          </div>}
      </div>
    </div>;
}